import {CarInfoInputModel} from "@/model/car-info/car-info-input.model";
import {CarInfoInputUtils} from "./car-info-input-utils";
import {CarInfoInputChecker} from "./car-info-input-checker";
import {CommonConstant} from "@/common-constant";

export class CarInfoInputCascadeManager {

    private static readonly INITIALS: Array<any> = [
        {id: 1, name: 'ア行'},
        {id: 2, name: 'カ行'},
        {id: 3, name: 'サ行'},
        {id: 4, name: 'タ行'},
        {id: 5, name: 'ナ行'},
        {id: 6, name: 'ハ行'},
        {id: 7, name: 'マ行'},
        {id: 8, name: 'ヤ行'},
        {id: 9, name: 'ラ行'},
        {id: 10, name: 'ワ行'},
        {id: 11, name: '英数'},
        {id: 12, name: 'その他'}
    ];

    /**
     * メーカーパネルを選択した際の処理を実行します。
     * @param model 車両情報
     * @param name 入力・選択した項目の値
     * @param initialize
     */
    public static async selectMaker(model: CarInfoInputModel, name: string, initialize: boolean = false) {
        // 変更がない場合は処理を終了
        if (!initialize && model.maker === name) return;

        // 選択したメーカを設定
        model.maker = name;

        // 入力済みメーカがマスタに存在しない場合、メーカー入力欄を空にし処理を終了する
        const maker = model.makerMaster.makers.filter(ele => ele.name === name);
        if (!maker[0]) {
            model.maker = "";
            await this.selectCarTypeInitial(model, null);
            await this.selectCarType(model, null);
            CarInfoInputChecker.checkMaker(model);
            return;
        }

        // メーカーに対応する車種頭文字を取得する
        const makerId: number = maker[0].id;
        model.carTypeMaster.carTypes = await CarInfoInputUtils.getCarTypes(makerId);

        const initials = []
        new Set(model.carTypeMaster.carTypes.map(data => data.initial)).forEach(data => initials.push({
            id: data,
            name: data
        }))
        model.carTypeMaster.carTypeInitialsLinkedMaker = initials;

        if (!initialize) {
            // カスケードリストのリセット
            await this.selectCarTypeInitial(model, null);
            await this.selectCarType(model, null);
        }

        // メーカーの入力チェック
        CarInfoInputChecker.checkMaker(model);
    }

    /**
     * 頭文字を選択した際の処理を実行します。
     * @param model 車両情報
     * @param name 入力・選択した項目の値
     * @param initialize
     */
    public static async selectCarTypeInitial(model: CarInfoInputModel, name: string, initialize: boolean = false) {
        // 変更がない場合は処理を終了
        if (!initialize && model.typeInitialLetter === name) return;

        if (name) {
            // 頭文字を設定
            model.typeInitialLetter = name;
            model.carTypeMaster.carTypesLinkedInitial = model.carTypeMaster.carTypes.filter(data => data.initial === name).map(data => (
                {id: data.id, name: data.name}
            ));
        } else {
            // 頭文字を設定
            model.typeInitialLetter = "";

            // 頭文字に対応する車種を取得する
            model.carTypeMaster.carTypesLinkedInitial = [];
        }
    }

    /**
     * 車種パネルを選択した際の処理を実行します。
     * @param model 車両情報
     * @param name 入力・選択した項目の値
     * @param initialize
     */
    public static async selectCarType(model: CarInfoInputModel, name: string, initialize: boolean = false) {
        // 変更がない場合は処理を終了
        if (!initialize && model.type === name) return;

        // 入力済みメーカがマスタに存在しない場合、または、入力済み車種がマスタに存在しない場合
        // 車種入力欄を空にし処理を行うように値を再設定する
        let carType = name;
        if (!model.makerMaster.makers.find(ele => ele.name === model.maker) ||
            !model.carTypeMaster.carTypes.find(data => data.name === name)) {
            carType = "";
        }


        // 選択時
        if(carType) {
            // 選択した車種を設定
            model.type = carType;
            // 車種にその他が選択された場合、年式のカスケードリストを作成しない（車種詳細リストにその他に紐づくデータはないため）
            if (model.type != "その他") {
                const carTypeDetailId = model.carTypeMaster.carTypes.find(data => data.name === name).id
                model.carTypeMaster.carTypeDetails = await CarInfoInputUtils.getCarTypeDetails(carTypeDetailId)

                // 終売年のない年式を取得
                const notHasFinalYearList = Array.from(new Set(
                    model.carTypeMaster.carTypeDetails.filter(data => !data.modelFinalYear).map(data => CarInfoInputUtils.formatModelYear(data)))
                ).sort().reverse();
                // 終売年のある年式を取得
                const hasFinalYearList = Array.from(new Set(
                    model.carTypeMaster.carTypeDetails.filter(data => !!data.modelFinalYear).map(data => CarInfoInputUtils.formatModelYear(data)))
                ).sort().reverse();

                model.carTypeMaster.modelYears = [""].concat(notHasFinalYearList).concat(hasFinalYearList);
            } else {
                // 車種にその他選択時
                model.carTypeMaster.modelYears = [];
            }

            if (!initialize) {
                // 年式を初期化
                model.modelYear = "";
                model.model = "";
                model.frontTireSize = ""
                model.reaTireSize = ""
            }

            // タイヤパネル初期化
            model.tireMaster.frontTireSizeList = []
            model.tireMaster.reaTireSizeList = []
        }
        // 選択解除時
        else {
            model.type = "";
            // 年式・年式リストを初期化
            model.carTypeMaster.modelYears = [];
        }
        if (!initialize) {
            // カスケードリストのリセット
            this.selectModelYear(model, null);
        }
        // 車種の入力チェック
        CarInfoInputChecker.checkType(model);
        // 年式の選択不可制御
        model.modelYearDisabled = model.carTypeMaster.modelYears.length < 1;
    }

    /**
     * 年式を選択した際の処理を実行します。
     * @param model 車両情報
     * @param modelYear 選択された年式
     * @param initialize
     */
    public static selectModelYear(model: CarInfoInputModel, modelYear: string, initialize: boolean = false) {

        // 入力済みメーカがマスタに存在しない場合、または、入力済み車種がマスタに存在しない場合
        // 年式を空にし処理を行うように値を再設定する
        let modelYearTmp = modelYear;
        if (!model.makerMaster.makers.find(ele => ele.name === model.maker) ||
            !model.carTypeMaster.carTypes.find(data => data.name === model.type)) {
            modelYearTmp = "";
        }

        // 値を選択
        if (modelYearTmp) {
            // 年式を設定
            model.modelYear = modelYearTmp;
            const carTypeDetails = this.getCarDetailsByModelYear(model, modelYearTmp)
            // 年式に対応するグレード・型式を取得する
            model.carTypeMaster.gradeModels = carTypeDetails.map(data => CarInfoInputUtils.formatGradeModelsLabel(data.grade, data.modelSymbol, data.model, data.driveSystem))
                .filter((elem, index, self) => self.indexOf(elem) === index)
                .map(label => ({name: label, value: label, id: label}));
            model.carTypeMaster.gradeModels = [{name: "", value: "", id: null}].concat(model.carTypeMaster.gradeModels);


            // 年式に対応するタイヤサイズを取得する
            model.tireMaster.frontTireSizeList = this.getTireSize(carTypeDetails, true);
            model.tireMaster.reaTireSizeList = this.getTireSize(carTypeDetails, false);

            // タイヤサイズパネルを初期化。既に選択されており更新されたタイヤマスタにも存在する場合は初期化しない。
            if (!model.tireMaster.frontTireSizeList.find(val => val.size === model.frontTireSize)) {
                model.frontTireSize = "";
            }
            if (!model.tireMaster.reaTireSizeList.find(val => val.size === model.reaTireSize)) {
                model.reaTireSize = "";
            }
        }
        // 選択解除
        else {
            // 年式を設定
            model.modelYear = "";

            // 年式に対応するグレード・型式、タイヤサイズを取得する
            model.carTypeMaster.gradeModels = [];

            // タイヤサイズパネルを初期化
            model.frontTireSize = "";
            model.reaTireSize = "";
        }

        if (!initialize) {
            // グレード・型式・駆動式をクリア
            model.model = "";
            // カスケードリストのリセット
            this.selectGradeModel(model, "");
        }

        // グレード・型式・駆動式の選択不可制御
        model.gradeModelDisabled = model.carTypeMaster.gradeModels.length < 1;
    }

    /**
     * グレード・型式を選択した際の処理を実行します。
     * @param model 車両情報
     * @param gradeModel 選択されたグレード・型式
     * @param initialize
     */
    public static selectGradeModel(model: CarInfoInputModel, gradeModel: string, initialize: boolean = false) {
        // グレード・モデルに対応するタイヤサイズを取得する
        if (gradeModel) {
            const carTypeDetails = CarInfoInputUtils.createFilteredTireSizeList(model)
            model.tireMaster.frontTireSizeList = this.getTireSize(carTypeDetails, true);
            model.tireMaster.reaTireSizeList = this.getTireSize(carTypeDetails, false);

            if(!initialize) {
                // ボディタイプを取得する
                model.bodyType = carTypeDetails[0].bodyType;
            }
        } else {
            const carTypeDetails = this.getCarDetailsByModelYear(model, model.modelYear);
            model.tireMaster.frontTireSizeList = this.getTireSize(carTypeDetails, true);
            model.tireMaster.reaTireSizeList = this.getTireSize(carTypeDetails, false);
            // ボディタイプを初期化する
            model.bodyType = "";
        }
        // フロントタイヤパネルの再設定
        this.resetFrontTireSize(model);

        // タイヤサイズパネルを初期化。既に選択されており更新されたタイヤマスタにも存在する場合は初期化しない。
        if (!model.tireMaster.frontTireSizeList.find(val => val.size === model.frontTireSize)) {
            model.frontTireSize = "";
        }
        if (!model.tireMaster.reaTireSizeList.find(val => val.size === model.reaTireSize)) {
            model.reaTireSize = "";
        }
    }

    /**
     * 前タイヤサイズを選択した際の処理を実行します。
     * @param model 車両情報
     * @param name 入力・選択した項目の値
     */
    public static selectFrontTireSize(model: CarInfoInputModel, name: string) {
        model.frontTireSize = name;
        // リム径、幅、扁平率をリセットする
        model.frontRimDiameter = "";
        model.frontTireWidth = "";
        model.frontTireAspectRatio = "";
        model.frontXL = false
        model.frontRft = false
    }

    /**
     * 後タイヤサイズを選択した際の処理を実行します。
     * @param model 車両情報
     * @param name 入力・選択した項目の値
     */
    public static selectReaTireSize(model: CarInfoInputModel, name: string) {
        model.reaTireSize = name;
        // リム径、幅、扁平率をリセットする
        model.reaRimDiameter = "";
        model.reaTireWidth = "";
        model.reaTireAspectRatio = "";
        model.reaXL = false
        model.reaRft = false
    }

    /**
     * お車情報登録時の各マスタを設定します。
     * @param model 登録時のお車情報
     */
    public static async setCarMasterForRegister(model: CarInfoInputModel) {
        // modelのコピーを作成
        const copy: CarInfoInputModel = new CarInfoInputModel;
        copy.copy(model);

        // メーカーを設定
        model.makerMaster.makers = await CarInfoInputUtils.getMakers();

        // 初期表示用車種頭文字を設定
        model.carTypeMaster.carTypeInitials = this.INITIALS;

        // メーカーの初期設定
        if (copy.maker) {
            await CarInfoInputCascadeManager.selectMaker(model, copy.maker, true);
        }

        // 車種頭文字の初期設定
        if (copy.typeInitialLetter || copy.type) {
            // 車種マスタを設定する
            await CarInfoInputCascadeManager.selectCarTypeInitial(model, copy.typeInitialLetter, true);
        }

        // 車種の初期設定
        if (copy.type) {
            await CarInfoInputCascadeManager.selectCarType(model, copy.type, true);
        }

        // 前後で異なるサイズのタイヤを利用している場合、前後で異なるサイズのタイヤを利用しているフラグを立てる
        model.showBothTireFlag = false; // フラグは一旦落とす
        if ((model.frontTireSize != model.reaTireSize) ||
            ((model.frontRimDiameter != model.reaRimDiameter) ||
                (model.frontTireWidth != model.reaTireWidth) ||
                (model.frontTireAspectRatio != model.reaTireAspectRatio) ||
                (model.frontXL != model.reaXL) ||
                (model.frontRft != model.reaRft)
            )) {
            model.showBothTireFlag = true;
        }

        // 年式の初期化
        if (copy.modelYear) {
            CarInfoInputCascadeManager.selectModelYear(model, copy.modelYear, true);
        }

        // グレード・型式・駆動式の初期化
        if (copy.model) {
            CarInfoInputCascadeManager.selectGradeModel(model, copy.model, true);
        }

        // タイヤサイズパネルの初期化
        model.frontTireSize = copy.frontTireSize;
        model.reaTireSize = copy.reaTireSize;


        // タイヤサイズがタイヤサイズパネルに存在するかチェックし存在する場合は手入力値をクリア、存在する場合はタイヤサイズをクリア
        const frontTireSize = model.tireMaster.frontTireSizeList.find(tireSize => {
            const detail = model.carTypeMaster.carTypeDetails.find(item => item.carTypeTireSizeId === tireSize.id);
            return (
                // 幅、扁平率、リム径、ラジアル、XL、RTFが一致するのを取得
                detail.tireWidth == model.frontTireWidth &&
                detail.aspectRatio == model.frontTireAspectRatio &&
                detail.rimDiameter == model.frontRimDiameter &&
                detail.radial == model.frontTireRadial &&
                detail.xl == model.frontXL.toString() &&
                detail.rft == model.frontRft.toString()
            )
        })
        if (frontTireSize) {
            // タイヤサイズパネルのラベルをセット
            model.frontTireSize = frontTireSize.size
            // 存在する場合、手入力値をクリア
            model.frontTireWidth = "";
            model.frontTireAspectRatio = "";
            model.frontTireRadial = "";
            model.frontRimDiameter = "";
            model.frontXL = false;
            model.frontRft = false;
        } else {
            // 存在しない場合、タイヤサイズをクリア
            model.frontTireSize = "";
        }
        const rearTireSize = model.tireMaster.reaTireSizeList.find(tireSize => {
            const detail = model.carTypeMaster.carTypeDetails.find(item => item.carTypeTireSizeId === tireSize.id);
            return (
                // 幅、扁平率、リム径、ラジアル、XL、RTFが一致するのを取得
                detail.tireWidth == model.reaTireWidth &&
                detail.aspectRatio == model.reaTireAspectRatio &&
                detail.rimDiameter == model.reaRimDiameter &&
                detail.radial == model.reaTireRadial &&
                detail.xl == model.reaXL.toString() &&
                detail.rft == model.reaRft.toString()
            )
        })
        if (rearTireSize) {
            // タイヤサイズパネルのラベルをセット
            model.reaTireSize = rearTireSize.size
            // 存在する場合、手入力値をクリア
            model.reaTireWidth = "";
            model.reaTireAspectRatio = "";
            model.reaTireRadial = "";
            model.reaRimDiameter = "";
            model.reaXL = false;
            model.reaRft = false;
        } else {
            // 存在しない場合、タイヤサイズをクリア
            model.reaTireSize = "";
        }

        // メーカーがその他の場合
        if (model.maker) {
            const makerId = model.makerMaster.makers.filter(ele => ele.name === model.maker)[0].id;
            if (makerId == "other_domestic_car" || makerId == "other_imported_car") {
                model.selectedOtherMaker = true; // フラグの設定
                model.type = CommonConstant.CAR_TYPE_OTHER; // 車種をその他に設定
                CarInfoInputCascadeManager.selectFrontTireSize(model, ""); // 前タイヤサイズのクリア
                CarInfoInputCascadeManager.selectReaTireSize(model, ""); // 後タイヤサイズのクリア
            }
        }
    }

    /**
     * 車両詳細を年式で絞って返却します。
     * @param model モデル
     * @param modelYear 年式
     */
    private static getCarDetailsByModelYear(model: CarInfoInputModel, modelYear:string):Array<any>{
        const year = modelYear.replace(/年/g, "")
        const years = year.split("〜")
        if (years[1] == "") {
            // 終売年なし
            return model.carTypeMaster.carTypeDetails.filter(data => data.modelYear == years[0] && !data.modelFinalYear)
        } else {
            //終売年あり
            return model.carTypeMaster.carTypeDetails.filter(data => data.modelYear == years[0] && data.modelFinalYear == years[1])
        }
    }

    /**
     * 引数の車両詳細リストから前後のタイヤサイズリストを取得します。
     * @param carTypeDetails 車両詳細リスト
     * @param isFront 前輪かどうかのフラグ
     */
    private static getTireSize(carTypeDetails: Array<any>, isFront: boolean = true): Array<any> {
        // 前輪の場合は1,後輪の場合は2(0は両方)
        const frType = isFront ? 1 : 2;
        const nonXlList = carTypeDetails.filter(data => data.frType == 0 || data.frType == frType).map(data => (
            {
                id: data.carTypeTireSizeId,  // タイヤサイズマスタID
                size: CarInfoInputUtils.formatTireSize(data.tireWidth, data.aspectRatio, data.radial, data.rimDiameter, "", data.rft), // XLは表示しない
                tireWidth: data.tireWidth,
                aspectRatio: data.aspectRatio,
                radial: data.radial,
                rimDiameter: data.rimDiameter,
                xl: data.xl,
                rft: data.rft
            }));

        // 表示用のリストに再生成して返す
        return nonXlList.map(data => {
            // 表示に必要なパネルデータを生成
            const panelData = {id: data.id, size: data.size};
            // XLあり/なしが両方存在する場合はXLをつける
            // ※ 「XL（エクストラロード）」 かつ 「異なるレコード（IDが異）で同サイズ、XL（エクストラロード）以外」が存在する
            if ((data.xl == "true") && (typeof nonXlList.find(other => (other.id !== data.id) && (other.size === data.size) && (other.xl !== "true")) !== 'undefined')) {
                panelData.size = CarInfoInputUtils.formatTireSize(data.tireWidth, data.aspectRatio, data.radial, data.rimDiameter, data.xl, data.rft);
            }
            return panelData;
        })
            // 重複排除
            .filter((elem, index, self) => self.findIndex(data => data.size === elem.size) === index)
            // ソート
            .sort((data1, data2) => data1.size < data2.size ? -1 : 1);
    }

    /**
     * 「前後で異なるサイズのタイヤを使用している場合はチェック」の状態によってフロントタイヤとリアタイヤのタイヤサイズパネルで重複するもののみを再設定します。
     * @param model
     */
    public static resetFrontTireSize(model: CarInfoInputModel){
        // 「前後で異なるサイズのタイヤを使用している場合はチェック」にチェックがない場合
        if (!model.showBothTireFlag) {
            // 事前に各処理で前/後タイヤサイズリストは重複が省かれているので連結した際に重複するサイズのみ抽出し前タイヤサイズリストに再設定する
            let frontRear = model.tireMaster.frontTireSizeList.concat(model.tireMaster.reaTireSizeList)
            model.tireMaster.frontTireSizeList = frontRear.filter((elem, index, self) => !(self.findIndex(data => data.size === elem.size) === index))
        }
    }

    /**
     * 「前後で異なるサイズのタイヤを使用している場合はチェック」のチェックの際の処理。
     * @param model
     * @param checkbox
     * @param registerMode
     */
    public static changeCarTireCheckbox(model: CarInfoInputModel, checkbox, registerMode){
        if (!checkbox.target.checked) {
            // チェックが外れた場合は後輪の設定値をクリア
            model.reaTireSize = "";
            model.reaTireWidth = "";
            model.reaTireAspectRatio = "";
            model.reaRimDiameter = "";
            model.reaXL = false;
            model.reaRft = false;
            model.errorMessage.reaFreeInputErrorMsg = "";
        } else {
            // チェックされた場合
            if (!registerMode && model.id) {
                // お車情報編集の場合は登録されているタイヤサイズをセットする
                if (model.reaTireSizeHolder.reaTireSize) {
                    model.reaTireSize = model.reaTireSizeHolder.reaTireSize;
                } else {
                    model.reaTireWidth = model.reaTireSizeHolder.reaTireWidth;
                    model.reaTireAspectRatio = model.reaTireSizeHolder.reaTireAspectRatio;
                    model.reaRimDiameter = model.reaTireSizeHolder.reaRimDiameter;
                    model.reaXL = model.reaTireSizeHolder.reaXL;
                    model.reaRft = model.reaTireSizeHolder.reaRft;
                }
            }
        }

        let carTypeDetails;
        if (model.model) {
            // グレード・型式・駆動式まで入力されている場合
            carTypeDetails = CarInfoInputUtils.createFilteredTireSizeList(model);
        } else if (model.modelYear) {
            // 年式まで入力されている場合
            carTypeDetails = this.getCarDetailsByModelYear(model, model.modelYear);
        } else {
            // タイヤサイズパネルの表示条件の入力まで達していない場合
            model.tireMaster.frontTireSizeList = [];
            model.tireMaster.reaTireSizeList = [];
            return;
        }
        // 前後のタイヤサイズリストを取得する
        model.tireMaster.frontTireSizeList = this.getTireSize(carTypeDetails, true);
        model.tireMaster.reaTireSizeList = this.getTireSize(carTypeDetails, false);
        // 前タイヤサイズパネルのみの表示時に後タイヤサイズパネルと重複したパネルのみを表示するようにタイヤサイズリストを再設定する
        this.resetFrontTireSize(model);
    }
}